/**
 * L'état d'interface de l'onglet « Offres de cours » : l'onglet demandé, la feuille d'une offre
 * (son étape), les horaires cochés d'un changement de créneau, la réponse en route et son refus.
 * Pur, testé.
 *
 * Port de `ProposalsTabScreen.js` (`activeTab`, `loadingItems`, les deux `DialogM` enchaînés, et la
 * sélection que `handleSlotChange` repassait à chaque `DialogM.show`) : la feuille reste ouverte
 * pendant l'envoi (le bouton tourne, la carte dit « Envoi en cours… ») et se ferme à la réponse ; un
 * refus se dit DANS l'étape (l'audit : « déplacé »), plus en boîte par-dessus.
 * Une offre demandée par « Ma journée » (« Accepter », « Voir ») s'ouvre dès qu'elle est dans la liste.
 */
import { type UITutorOffer, type UITutorOfferFlowStep, type UITutorOfferKind } from "../../../types/tutorDispoOffres"
import { type TutorOfferAnswer } from "./useTutorOfferAnswers"

// ── Le choix des horaires d'un changement de créneau (`slotChangeProposal.js`) ──

/** `toggleSlot` : remplace le choix quand un seul horaire est à prendre, ne dépasse jamais `pick` sinon. */
export function toggleSlotChoice(selected: number[], liveSessionId: number, pick: number): number[] {
    if (selected.includes(liveSessionId)) return selected.filter((id) => id !== liveSessionId)
    if (pick === 1) return [liveSessionId]
    if (selected.length >= pick) return selected
    return [...selected, liveSessionId]
}

/**
 * Le corps de l'acceptation (`buildAcceptBody`, sans le statut que la réponse écrit elle-même) : les
 * horaires choisis d'un changement de créneau, rien pour une autre offre. Rien ne part tant qu'il n'y en
 * a pas exactement `pick` (`canAccept`) : `null`.
 */
export function tutorOfferAcceptBody(offer: UITutorOffer, selected: number[]): Record<string, unknown> | null {
    if (offer.slotChange == null) return {}
    const { pick } = offer.slotChange
    return pick > 0 && selected.length === pick ? { live_session_ids: [...selected] } : null
}

export type ToggleSlotAction = { type: "TOGGLE_SLOT"; liveSessionId: number; pick: number }

/** Les props du choix des horaires de `<TutorOfferFlow>`, les mêmes pour l'onglet et la page d'un lien. */
export function slotChoiceFlowProps(
    offer: UITutorOffer,
    selected: number[],
    dispatch: (action: ToggleSlotAction) => void
) {
    return {
        selectedSlotIds: selected,
        canAcceptSlots: tutorOfferAcceptBody(offer, selected) != null,
        onToggleSlot: (liveSessionId: number) =>
            dispatch({ type: "TOGGLE_SLOT", liveSessionId, pick: offer.slotChange?.pick ?? 0 }),
    }
}

export type TutorOffersOpenRequest = { offerId: number; step: UITutorOfferFlowStep }

export type TutorOffersUiState = {
    requestedKind: UITutorOfferKind | null
    flow: { offerId: number; step: UITutorOfferFlowStep } | null
    /** Les horaires cochés d'un changement de créneau ouvert (`live_session_id`). */
    slotSelection: number[]
    sending: { offerId: number; answer: TutorOfferAnswer } | null
    flowError: string | null
    /** Une offre que « Ma journée » a demandé d'ouvrir, pas encore trouvée dans la liste. */
    pendingOpen: TutorOffersOpenRequest | null
}

export type TutorOffersUiAction =
    | { type: "SELECT_TAB"; kind: UITutorOfferKind }
    | { type: "OPEN_FLOW"; offerId: number; step: UITutorOfferFlowStep; slotSelection?: number[] }
    | { type: "GO_STEP"; step: UITutorOfferFlowStep }
    | ToggleSlotAction
    | { type: "CLOSE_FLOW" }
    | { type: "SENDING"; offerId: number; answer: TutorOfferAnswer }
    | { type: "SENT" }
    | { type: "FAILED"; message: string }
    | { type: "REQUEST_OPEN"; kind: UITutorOfferKind | null; request: TutorOffersOpenRequest }

export const initialTutorOffersUiState: TutorOffersUiState = {
    requestedKind: null,
    flow: null,
    slotSelection: [],
    sending: null,
    flowError: null,
    pendingOpen: null,
}

export function tutorOffersUiReducer(state: TutorOffersUiState, action: TutorOffersUiAction): TutorOffersUiState {
    switch (action.type) {
        case "SELECT_TAB":
            return { ...state, requestedKind: action.kind, pendingOpen: null }
        case "OPEN_FLOW":
            return {
                ...state,
                flow: { offerId: action.offerId, step: action.step },
                slotSelection: action.slotSelection ?? [],
                flowError: null,
                pendingOpen: null,
            }
        case "GO_STEP":
            return state.flow == null
                ? state
                : { ...state, flow: { ...state.flow, step: action.step }, flowError: null }
        case "TOGGLE_SLOT":
            return state.flow == null
                ? state
                : {
                      ...state,
                      slotSelection: toggleSlotChoice(state.slotSelection, action.liveSessionId, action.pick),
                      flowError: null,
                  }
        case "CLOSE_FLOW":
            return { ...state, flow: null, slotSelection: [], flowError: null }
        case "SENDING":
            return { ...state, sending: { offerId: action.offerId, answer: action.answer }, flowError: null }
        case "SENT":
            return { ...state, sending: null, flow: null, slotSelection: [], flowError: null }
        case "FAILED":
            return { ...state, sending: null, flowError: action.message }
        case "REQUEST_OPEN":
            return { ...state, requestedKind: action.kind ?? state.requestedKind, pendingOpen: action.request }
        default:
            return state
    }
}

/** L'offre que la feuille montre ; `null` quand elle n'est plus dans la liste : la feuille ne se montre pas. */
export function tutorOfferInFlow(state: TutorOffersUiState, offers: UITutorOffer[]): UITutorOffer | null {
    if (state.flow == null) return null
    return offers.find((offer) => offer.id === state.flow?.offerId) ?? null
}

/** L'offre demandée par « Ma journée », si la liste la montre en attente. */
export function tutorOfferToOpen(state: TutorOffersUiState, offers: UITutorOffer[]): UITutorOffer | null {
    const wanted = state.pendingOpen
    if (wanted == null) return null
    return offers.find((offer) => offer.id === wanted.offerId && offer.status === "pending") ?? null
}
